// Pure time-entry rules shared by api (persist) and web (live feedback). Inputs are ISO UTC
// timestamps; the api rounds to whole minutes before calling these.
import type { WarningCode, Weekday } from "../enums.js";
import {
  addDaysIso,
  type IsoDate,
  minutesBetween,
  parseTimestamp,
  roundToMinute,
  toBerlinDate,
  weekdayOf,
} from "../time.js";
import type { AssignmentHours } from "./absence.js";

export const MAX_SHIFT_MINUTES = 24 * 60;
export const MAX_COMMENT_LENGTH = 100;
export const WORK_DATE_PAST_DAYS = 60;
export const WORK_DATE_FUTURE_DAYS = 1;
export const BREAK_REQUIRED_AFTER_6H = 30;
export const BREAK_REQUIRED_AFTER_9H = 45;
export const SHIFT_TOO_LONG_MINUTES = 10 * 60;
export const ASSIGNMENT_HOURS_TOLERANCE_MINUTES = 120;

export type BreakInput = { startedAt: string; endedAt: string };

export type EntryInput = {
  workDate: IsoDate;
  startedAt: string;
  endedAt: string;
  breaks: BreakInput[];
  comment?: string | null;
};

export type EntryInterval = { id?: string; startedAt: string; endedAt: string };

export type EntryContext = {
  today: IsoDate;
  // The assignment the entry belongs to; undefined when none is active on the work date.
  assignment?: {
    dateFrom: IsoDate;
    dateTo: IsoDate;
    endedAt?: string | null;
    hours: AssignmentHours;
  };
  // Other non-superseded entries of the employee that could overlap (same day ±1).
  otherEntries?: EntryInterval[];
  // A whole-day approved/synced absence covers the work date.
  absenceOnDay?: boolean;
  assignmentChanged?: boolean;
  // Dispatcher corrections of existing rows are not bound to the work-date window.
  skipWorkDateWindow?: boolean;
  // Submitting an existing row: a changed/ended assignment is the `assignment_changed` warning,
  // not a block (the row was valid when it was created).
  skipAssignmentCheck?: boolean;
};

export const ENTRY_ERROR_CODES = [
  "end_before_start",
  "too_long",
  "break_outside_shift",
  "breaks_overlap",
  "overlap",
  "no_active_assignment",
  "comment_too_long",
  "work_date_out_of_window",
  "invalid_timestamp",
  "no_net_time",
] as const;
export type EntryErrorCode = (typeof ENTRY_ERROR_CODES)[number];

// field → error code; empty when valid.
export type FieldErrors = Record<string, EntryErrorCode>;

function parse(value: string): Date | null {
  const parsed = parseTimestamp(value);
  return parsed ? roundToMinute(parsed) : null;
}

export function breakMinutes(breaks: BreakInput[]): number {
  return breaks.reduce((sum, b) => {
    const s = parse(b.startedAt);
    const e = parse(b.endedAt);
    return s && e ? sum + Math.max(0, minutesBetween(s, e)) : sum;
  }, 0);
}

export function grossMinutes(input: Pick<EntryInput, "startedAt" | "endedAt">): number {
  const s = parse(input.startedAt);
  const e = parse(input.endedAt);
  return s && e ? minutesBetween(s, e) : 0;
}

export function netMinutes(input: Pick<EntryInput, "startedAt" | "endedAt" | "breaks">): number {
  return Math.max(0, grossMinutes(input) - breakMinutes(input.breaks));
}

export function validateEntry(input: EntryInput, ctx: EntryContext): FieldErrors {
  const errors: FieldErrors = {};
  const start = parse(input.startedAt);
  const end = parse(input.endedAt);
  if (!start) errors.startedAt = "invalid_timestamp";
  if (!end) errors.endedAt = "invalid_timestamp";
  if (!start || !end) return errors;

  if (end.getTime() <= start.getTime()) errors.endedAt = "end_before_start";
  else if (minutesBetween(start, end) > MAX_SHIFT_MINUTES) errors.endedAt = "too_long";

  const sorted = input.breaks
    .map((b, index) => ({ index, s: parse(b.startedAt), e: parse(b.endedAt) }))
    .sort((a, b) => (a.s?.getTime() ?? 0) - (b.s?.getTime() ?? 0));
  let previousEnd: Date | null = null;
  for (const b of sorted) {
    const key = `breaks.${b.index}`;
    if (!b.s || !b.e) {
      errors[key] = "invalid_timestamp";
      continue;
    }
    if (
      b.e.getTime() <= b.s.getTime() ||
      b.s.getTime() < start.getTime() ||
      b.e.getTime() > end.getTime()
    ) {
      errors[key] = "break_outside_shift";
      continue;
    }
    if (previousEnd && b.s.getTime() < previousEnd.getTime()) errors[key] = "breaks_overlap";
    previousEnd = b.e;
  }

  // Breaks that cover the whole shift leave nothing to book (Come 06:00 / Go 06:00 …); the
  // error sits on the last break, the field the employee has to change.
  if (
    !errors.endedAt &&
    input.breaks.length > 0 &&
    !Object.keys(errors).some((k) => k.startsWith("breaks.")) &&
    netMinutes(input) <= 0
  )
    errors[`breaks.${input.breaks.length - 1}`] = "no_net_time";

  if (ctx.otherEntries) {
    const overlaps = ctx.otherEntries.some((other) => {
      const os = parse(other.startedAt);
      const oe = parse(other.endedAt);
      return os && oe && os.getTime() < end.getTime() && start.getTime() < oe.getTime();
    });
    if (overlaps) errors.startedAt = "overlap";
  }

  if (!ctx.skipAssignmentCheck && !assignmentCovers(ctx, input.workDate)) {
    errors.assignmentId = "no_active_assignment";
  }

  if ((input.comment ?? "").trim().length > MAX_COMMENT_LENGTH) errors.comment = "comment_too_long";

  if (!ctx.skipWorkDateWindow && !isWithinWorkDateWindow(input.workDate, ctx.today))
    errors.workDate = "work_date_out_of_window";

  return errors;
}

// The context's assignment exists, is not ended and covers the work date.
export function assignmentCovers(
  ctx: Pick<EntryContext, "assignment">,
  workDate: IsoDate,
): boolean {
  const a = ctx.assignment;
  return a !== undefined && a.endedAt == null && a.dateFrom <= workDate && workDate <= a.dateTo;
}

// Entries may be created for today − 60 … today + 1 days.
export function isWithinWorkDateWindow(workDate: IsoDate, today: IsoDate): boolean {
  return (
    workDate >= addDaysIso(today, -WORK_DATE_PAST_DAYS) &&
    workDate <= addDaysIso(today, WORK_DATE_FUTURE_DAYS)
  );
}

// Warnings never block; they are stored with the entry and shown to both roles.
export function computeWarnings(input: EntryInput, ctx: EntryContext): WarningCode[] {
  const warnings: WarningCode[] = [];
  const net = netMinutes(input);
  const pause = breakMinutes(input.breaks);
  if (net > 9 * 60 && pause < BREAK_REQUIRED_AFTER_9H) warnings.push("break_too_short");
  else if (net > 6 * 60 && pause < BREAK_REQUIRED_AFTER_6H) warnings.push("break_too_short");
  if (net > SHIFT_TOO_LONG_MINUTES) warnings.push("shift_too_long");
  if (ctx.absenceOnDay) warnings.push("absence_conflict");
  if (ctx.assignment) {
    const weekday: Weekday = weekdayOf(input.workDate);
    const target = Math.round(ctx.assignment.hours[weekday] * 60);
    if (Math.abs(net - target) > ASSIGNMENT_HOURS_TOLERANCE_MINUTES) {
      warnings.push("outside_assignment_hours");
    }
  }
  if (ctx.assignmentChanged) warnings.push("assignment_changed");
  return warnings;
}

// Berlin date of the shift start: shifts over midnight belong to the start day.
export function workDateOf(startedAt: string): IsoDate | null {
  const start = parse(startedAt);
  return start ? toBerlinDate(start) : null;
}
